/**
 * 修改管理员登录密码弹窗
 *
 * 已登录管理员修改自己的密码：输入原密码 + 新密码（至少6位）+ 确认。
 * 成功后弹出提示并自动关闭。
 */

import { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  Snackbar,
} from '@mui/material'
import { changeAdminPassword } from '@/api/services'

interface ChangePasswordDialogProps {
  /** 是否打开 */
  open: boolean
  /** 关闭回调 */
  onClose: () => void
}

export function ChangePasswordDialog({ open, onClose }: ChangePasswordDialogProps) {
  const [oldPwd, setOldPwd] = useState('')
  const [newPwd, setNewPwd] = useState('')
  const [confirmPwd, setConfirmPwd] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [success, setSuccess] = useState(false)

  const reset = () => {
    setOldPwd('')
    setNewPwd('')
    setConfirmPwd('')
    setError('')
    setSubmitting(false)
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleSubmit = async () => {
    setError('')
    if (!oldPwd) {
      setError('请输入原密码')
      return
    }
    if (newPwd.length < 6) {
      setError('新密码至少 6 位')
      return
    }
    if (newPwd !== confirmPwd) {
      setError('两次输入的新密码不一致')
      return
    }

    setSubmitting(true)
    try {
      await changeAdminPassword(oldPwd, newPwd)
      setSuccess(true)
      window.setTimeout(() => {
        setSuccess(false)
        handleClose()
      }, 1200)
    } catch (e) {
      const message = e instanceof Error ? e.message : '修改失败，请重试'
      setError(message || '修改失败，请重试')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, fontSize: '18px' }}>修改登录密码</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="原密码"
            type="password"
            size="small"
            fullWidth
            value={oldPwd}
            onChange={(e) => setOldPwd(e.target.value)}
            autoFocus
          />
          <TextField
            label="新密码"
            type="password"
            size="small"
            fullWidth
            value={newPwd}
            onChange={(e) => setNewPwd(e.target.value)}
            helperText="至少 6 位"
          />
          <TextField
            label="确认新密码"
            type="password"
            size="small"
            fullWidth
            value={confirmPwd}
            onChange={(e) => setConfirmPwd(e.target.value)}
          />
          {error ? (
            <Alert severity="error" sx={{ py: 0 }}>
              {error}
            </Alert>
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={handleClose} sx={{ color: '#666' }} disabled={submitting}>
          取消
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          sx={{
            backgroundColor: '#FF8C00',
            '&:hover': { backgroundColor: '#E67E00' },
          }}
        >
          {submitting ? '提交中...' : '确认修改'}
        </Button>
      </DialogActions>
      <Snackbar
        open={success}
        autoHideDuration={1200}
        onClose={() => setSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          密码修改成功
        </Alert>
      </Snackbar>
    </Dialog>
  )
}

export default ChangePasswordDialog
